import type { DevOnboardingCapability, Me } from "./generated/model";
import { isSessionExpired } from "./errors";

export const DEV_ONBOARDING_SESSION = "/api/v1/dev/onboarding/test-session";
const COOKIE_WRITES = new Set([
  DEV_ONBOARDING_SESSION,
  "/api/v1/me/sign-in/email/verify",
  "/api/v1/me/sign-in/google",
  "/api/v1/me/sign-in/google/complete",
  "/api/v1/me/sign-out",
  "/api/v1/me/sign-out-everywhere",
]);
const LOCK = "createdai-auth-cookie-transition-v1";

/** Local request intent, never an HTTP target account or an authorization credential. */
export interface AuthCookieIntent {
  subject: string;
  current: () => boolean;
  receiptRetry: boolean;
  issued: () => void;
}

export class AuthCookieSubjectChanged extends Error {
  override readonly name = "AuthCookieSubjectChanged";
  constructor() { super("The app session changed. Start again from the current session."); }
}

export function canCoordinateAuthCookies(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.locks?.request === "function";
}

/** One ordering seam for the existing SPA cookie writers. Authorization remains server-owned. */
export function coordinateAuthCookieRequest<T>(
  path: string,
  init: RequestInit,
  intent: AuthCookieIntent | undefined,
  send: (issued: RequestInit) => Promise<T>,
  me: (signal?: AbortSignal | null) => Promise<Me>,
  capability: (signal?: AbortSignal | null) => Promise<DevOnboardingCapability>,
): Promise<T> {
  if ((init.method ?? "GET").toUpperCase() !== "POST" || !COOKIE_WRITES.has(path)) return send(init);
  const launcher = path === DEV_ONBOARDING_SESSION;
  if (!canCoordinateAuthCookies()) {
    if (launcher) return Promise.reject(new Error("This browser cannot safely coordinate a dev test session."));
    return send(init);
  }
  if (launcher && !intent) return Promise.reject(new Error("A current app-session intent is required."));
  return navigator.locks.request(LOCK, init.signal ? { signal: init.signal } : {}, async () => {
    init.signal?.throwIfAborted();
    if (launcher && intent) {
      if (!intent.current()) throw new AuthCookieSubjectChanged();
      let subject: Me;
      try { subject = await me(init.signal); }
      catch (error) {
        if (isSessionExpired(error)) throw new AuthCookieSubjectChanged();
        throw error;
      }
      if (subject.id !== intent.subject) {
        // Only an already issued same-key retry may ask the server to acknowledge its exact
        // test-user receipt. The backend still rejects another test user/key without new state.
        if (!intent.receiptRetry) throw new AuthCookieSubjectChanged();
        const active = await capability(init.signal);
        if (active.test_identity !== true || active.available !== false) throw new AuthCookieSubjectChanged();
      }
      if (!intent.current()) throw new AuthCookieSubjectChanged();
      init.signal?.throwIfAborted();
      intent.issued();
    }
    // Once issued, consume the response even if the caller aborts. Releasing early could let a
    // later login finish before this response applies its cookie. Page teardown is not covered.
    const { signal: _waitingSignal, ...issued } = init;
    try { return await send(issued); }
    finally { init.signal?.throwIfAborted(); }
  });
}
